import { useState, useMemo } from "react";
import {
  TrashIcon,
  PlusIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import SetItemDetail from "components/order/RateOrder/SetItemDetail";
import AsyncItemSelect from "components/item/AsyncItemSelect";
import Input from "components/ui/Input";
import { THead, TBody, TR, TH, TD, ActionButton, Badge } from "components/ui/Table";
import { useGetBillingRate } from "http-hooks/pricelistdetail/usePricelistDetail";
import { useOrderHeader } from "hooks/order";
import useOrderStore from "store/order";

export default function SetOrderDetails() {
  const {
    orderHeader,
    rateOrderItems,
    setRateOrderItems,
    setItemDetailModalOpen,
    setEditingItemIndex,
    protectedCases,
  } = useOrderHeader();
  const protectedCaseSet = useMemo(
    () => new Set(protectedCases.map(Number)),
    [protectedCases]
  );

  const [visibleInputs, setVisibleInputs] = useState(
    Array(rateOrderItems.length).fill(false)
  );

  // Hook for fetching billing rate
  const { mutateAsync: getBillingRate } = useGetBillingRate();

  const handleVisibleInputs = (index) => {
    const newVisibleInputs = [...visibleInputs];
    newVisibleInputs[index] = !newVisibleInputs[index];
    setVisibleInputs(newVisibleInputs);
  };

  const addRowOnitems = () => {
    setVisibleInputs([...visibleInputs, false]);
    setRateOrderItems([
      ...rateOrderItems,
      {
        case_id: rateOrderItems[rateOrderItems.length - 1].case_id,
        itemname: null,
        rate: "",
        UOM: "",
        packratio: "",
        quantity: "",
        itemdetails: "",
      },
    ]);
  };

  const removeItem = (itemIndex) => {
    setVisibleInputs(
      visibleInputs.filter((val, itemIn) => itemIn !== itemIndex)
    );
    setRateOrderItems(
      rateOrderItems.filter((item, itemIn) => itemIn !== itemIndex)
    );
  };

  const handleInputChange = (itemIndex, event) => {
    const { name, value } = event.target;
    setRateOrderItems(
      rateOrderItems.map((item, itemIn) =>
        itemIndex === itemIn ? { ...item, [name]: value } : item
      )
    );
  };

  const updateItemDataAndRate = async (itemIndex, selectedOption) => {
    let billingRate = "";
    let rateFromPricelist = false;
    
    // Fetch billing rate if item is selected
    if (selectedOption?.value && orderHeader.ordvendid && orderHeader.pldate) {
      const billingData = await getBillingRate({
        vendorId: orderHeader.ordvendid,
        effectiveDate: orderHeader.pldate,
        itemId: selectedOption.value,
      });
      billingRate = billingData.billingrate || "";
      rateFromPricelist = parseFloat(billingRate) > 0;
    }

    // Update all item data in a single operation
    const updatedItems = rateOrderItems.map((item, itemIn) =>
      itemIndex === itemIn
        ? {
            ...item,
            itemname: selectedOption,
            UOM: selectedOption?.itemuom || "",
            packratio: selectedOption?.packratio || "",
            rate: selectedOption?.value ? billingRate : item.rate,
            rateReadonly: rateFromPricelist,
          }
        : item
    );
    setRateOrderItems(updatedItems);
  };

  const handleItemSelect = async (itemIndex, selectedOption) => {
    // Update item data and rate in one operation
    await updateItemDataAndRate(itemIndex, selectedOption);

    // Add new row if this was the last row and now has an item
    setTimeout(() => {
      addNewRowIfNeeded(itemIndex, !!selectedOption?.value);
    }, 1000);
  };

  // Get case info for individual case display in table
  const getCaseInfo = (caseId) => {
    let totalPack = 0;
    let totalQuantity = 0;

    rateOrderItems.forEach((item) => {
      if (item.case_id === caseId && item.packratio && item.quantity) {
        const packRatio = parseFloat(item.packratio) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        totalPack += packRatio * quantity;
        totalQuantity += quantity;
      }
    });

    if (totalQuantity === 0) return null;

    const percentage = Math.round(totalPack);
    const isEfficient = percentage >= 95 && percentage <= 105;

    return {
      percentage,
      totalQuantity,
      isEfficient,
      color: isEfficient ? "text-green-600" : "text-red-600",
    };
  };

  const addNewRowIfNeeded = (itemIndex, hasItem) => {
    const currentItems = useOrderStore.getState().rateOrderItems;
    const isLastRow = itemIndex === currentItems.length - 1;

    if (isLastRow && hasItem) {
      setVisibleInputs([...visibleInputs, false]);
      const newItems = [
        ...currentItems,
        {
          case_id: currentItems[currentItems.length - 1].case_id + 1,
          itemname: null,
          rate: "",
          UOM: "",
          packratio: "",
          quantity: "",
          itemdetails: "",
        },
      ];
      setRateOrderItems(newItems);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-8 overflow-x-auto">
      <SetItemDetail />

      {/* Hotkey hint */}
      <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
        <span>Press</span>
        <kbd className="inline-flex items-center rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-600 shadow-sm">]</kbd>
        <span>to open the Lot details modal</span>
      </div>

      <div className="bg-white shadow-xl rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-3">
          <h2 className="text-lg font-bold text-white">Order Items</h2>
          <p className="text-gray-300 text-xs mt-0.5">
            Add and manage items for this order
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <THead>
              <TR hover={false}>
                <TH first compact>Case ID</TH>
                <TH compact>Item Details</TH>
                <TH compact>Rate</TH>
                <TH compact>Pack Ratio</TH>
                <TH compact>Quantity</TH>
                <TH compact>UOM</TH>
                <TH compact>Case Info</TH>
                <TH last compact className="text-center">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {rateOrderItems.map((item, itemIndex) => {
                const isProtected = protectedCaseSet.has(Number(item.case_id));

                return (
                <TR
                  key={`item-row-${itemIndex}`}
                  className={isProtected ? "bg-amber-50" : ""}
                >
                  <TD first compact>
                    <Input
                      type="number"
                      name="case_id"
                      value={item.case_id}
                      onChange={(e) => handleInputChange(itemIndex, e)}
                      showLabel={false}
                      readOnly={isProtected}
                      min={1}
                      max={99}
                      className="w-14"
                    />
                  </TD>
                  <TD compact className="text-gray-900">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        {isProtected ? (
                          <div className="px-2 py-1.5 text-sm text-gray-700 bg-gray-200 rounded-md border-0 ring-1 ring-inset ring-gray-300">
                            {item.itemname?.label || "-"}
                          </div>
                        ) : (
                          <AsyncItemSelect
                            value={item.itemname}
                            onChange={(selectedOption) =>
                              handleItemSelect(itemIndex, selectedOption)
                            }
                            vendorId={orderHeader.ordvendid}
                            placeholder="Search and select item..."
                            className="min-w-0"
                          />
                        )}
                      </div>
                      {!isProtected && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingItemIndex(itemIndex);
                            setItemDetailModalOpen(true);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                          title="Edit item details"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                      )}
                      {item.itemdetails && item.itemdetails.trim() && (
                        <span
                          className="inline-flex items-center justify-center w-2 h-2 bg-blue-500 rounded-full ml-1"
                          title="Has comments"
                        ></span>
                      )}
                    </div>
                  </TD>
                  <TD compact>
                    <Input
                      type="number"
                      name="rate"
                      value={item.rate}
                      onChange={(e) => handleInputChange(itemIndex, e)}
                      showLabel={false}
                      readOnly={isProtected || !!item.rateReadonly}
                      step="0.01"
                      min={0}
                      placeholder="0.00"
                      className="w-20"
                    />
                  </TD>
                  <TD compact className="text-center">
                    <Badge variant="success">
                      {item.packratio || "-"}
                    </Badge>
                  </TD>
                  <TD compact>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-1">
                        <Input
                          type="number"
                          name="quantity"
                          value={item.quantity}
                          onChange={(e) => handleInputChange(itemIndex, e)}
                          showLabel={false}
                          readOnly={isProtected}
                          step="0.00001"
                          min={0}
                          placeholder="0"
                          className="w-20"
                        />

                        {!isProtected && (
                          <button
                            type="button"
                            className="flex justify-center items-center p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-all duration-200"
                            onClick={() => handleVisibleInputs(itemIndex)}
                            title="Toggle additional input"
                          >
                            <EllipsisVerticalIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>

                      {!isProtected && visibleInputs[itemIndex] && (
                        <Input
                          type="number"
                          name="quantity"
                          value={item.quantity}
                          onChange={(e) => handleInputChange(itemIndex, e)}
                          showLabel={false}
                          step="0.00001"
                          min={0}
                          placeholder="0"
                          className="w-20"
                        />
                      )}
                    </div>
                  </TD>
                  <TD compact className="text-center">
                    <Badge variant="info">
                      {item.UOM || "-"}
                    </Badge>
                  </TD>
                  <TD compact className="text-center">
                    {(() => {
                      const caseInfo = getCaseInfo(item.case_id);
                      return caseInfo ? (
                        <div className="bg-gray-50 rounded-md p-1 text-xs text-center space-y-0.5 border border-gray-200">
                          <div
                            className={`font-bold text-xs ${caseInfo.color}`}
                          >
                            {caseInfo.percentage}%
                          </div>
                          <div className="text-gray-600 font-medium text-xs">
                            {caseInfo.totalQuantity} {item.UOM || ""}
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-400 text-xs">-</div>
                      );
                    })()}
                  </TD>
                  <TD last compact className="text-center">
                    {isProtected ? (
                      <Badge variant="warning">Locked</Badge>
                    ) : (
                      <div className="flex justify-center">
                        <ActionButton
                          onClick={addRowOnitems}
                          variant="success"
                          title="Add new row"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </ActionButton>
                        <ActionButton
                          onClick={() => removeItem(itemIndex)}
                          variant="danger"
                          title="Remove row"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </ActionButton>
                      </div>
                    )}
                  </TD>
                </TR>
                );
              })}
            </TBody>
          </table>
        </div>
      </div>
    </div>
  );
}
